import {
  type DetachableRenderer,
  launchItemKey,
  type ModuleUiContext,
  type ModuleUiExports,
  type RailActivationHandler,
  type ResolvedModuleRuntimeUi,
  type ResolvedView,
  type TabHostController,
  type ViewComponent,
  type ViewTabChrome,
} from '@hibro/shared';

import { buildRuntimeUiUrlMap } from './runtime-ui-urls';

/** Lazy importer of a source module's renderer-side UI entry. */
export type LoadUi = () => Promise<ModuleUiExports>;

/**
 * Dynamically import a renderer UI module by URL (见 12). Used to load a
 * downloaded `source` module's UI bundle from `hibro-assets://<id>/<entry.ui>`, which is
 * absent from the shell's compile-time glob. Injected so the platform package
 * stays free of a hard-coded dynamic import (and is testable); the app shell
 * provides one Vite can leave un-analyzed (`/* @vite-ignore *\/`).
 */
export type ImportUiModule = (url: string) => Promise<ModuleUiExports>;

const defaultImportUiModule: ImportUiModule = (url) =>
  import(/* @vite-ignore */ url) as Promise<ModuleUiExports>;

/**
 * Renderer-side counterpart of the extension host's `ModuleManager` for UI
 * (架构方案 §5.7/§5.8). Source-module view code runs in the renderer process;
 * this registry discovers the owning module of a view and lazily activates its
 * UI entry the first time the view becomes visible (G5 异步懒加载), collecting
 * the React components registered via `registerView`. The `modules/*` glob that
 * produces `loaders` lives in the app shell, keeping this package
 * module-agnostic (延续 见 02 B.4 约束).
 */
export class UiRegistry {
  private readonly components = new Map<string, ViewComponent>();
  /** Domain controllers for views hosted in native tabbed workbench areas. */
  private readonly tabControllers = new Map<string, TabHostController>();
  /** Optional module-painted tab and right-header chrome, keyed by view id. */
  private readonly viewTabChrome = new Map<string, ViewTabChrome>();
  /** Render sources for detachable fragments (§5.8 通用 Detachable). */
  private readonly detachables = new Map<string, DetachableRenderer>();
  /**
   * Renderer-side contribution values a module bound via `registerContribution`
   * (见 17): point id -> entry key (`${moduleId}:${id}`) -> value (e.g. a React
   * component). Renderer-local: these never cross a process boundary.
   */
  private readonly contributionBindings = new Map<
    string,
    Map<string, unknown>
  >();
  /**
   * Re-activation handlers for activityBar items (见 21), keyed by launch item
   * key (`${moduleId}:${itemId}`) so the rail can look one up straight from the
   * projected item it is rendering.
   *
   * Not reclaimed per module like the maps above: a component registers these on
   * mount and disposes them on unmount, so entries come and go inside one module
   * activation and the disposer is the only thing that removes them.
   */
  private readonly railActivations = new Map<string, () => void>();
  private readonly activations = new Map<string, Promise<void>>();
  /** Loaded UI exports per activated module, kept to run `deactivate` (§5.4). */
  private readonly moduleExports = new Map<string, ModuleUiExports>();
  /** View ids each module registered, unregistered when the module deactivates. */
  private readonly registeredViews = new Map<string, Set<string>>();
  private readonly registeredTabControllers = new Map<string, Set<string>>();
  private readonly registeredViewTabChrome = new Map<string, Set<string>>();
  /** Fragment ids each module registered, unregistered on deactivation. */
  private readonly registeredDetachables = new Map<string, Set<string>>();
  /** `${point}::${key}` each module bound, unregistered on deactivation (见 17). */
  private readonly registeredContributions = new Map<string, Set<string>>();
  /** Visible view ids per module; the module's UI deactivates at zero. */
  private readonly visibleViews = new Map<string, Set<string>>();
  /** Modules activated for renderer-local controllers/contribution bindings. */
  private readonly residentModules = new Set<string>();

  constructor(
    private readonly loaders: Map<string, LoadUi>,
    private readonly views: ResolvedView[],
    private readonly importUiModule: ImportUiModule = defaultImportUiModule,
    runtimeUiEntries: readonly ResolvedModuleRuntimeUi[] = [],
  ) {
    this.runtimeUiUrls = buildRuntimeUiUrlMap(runtimeUiEntries);
  }

  private readonly runtimeUiUrls: ReadonlyMap<string, string>;

  getViewComponent(viewId: string): ViewComponent | undefined {
    return this.components.get(viewId);
  }

  getTabController(viewId: string): TabHostController | undefined {
    return this.tabControllers.get(viewId);
  }

  getViewTabChrome(viewId: string): ViewTabChrome | undefined {
    return this.viewTabChrome.get(viewId);
  }

  /** The render source for a detachable fragment, if its module registered one. */
  getDetachable(fragmentId: string): DetachableRenderer | undefined {
    return this.detachables.get(fragmentId);
  }

  /**
   * The renderer-side value a module bound to the contribution `key` under
   * `point` (见 17), or undefined if nothing was bound. Resolve the contributor's
   * UI first via {@link activateForModule} so the binding exists.
   */
  getContribution(point: string, key: string): unknown {
    return this.contributionBindings.get(point)?.get(key);
  }

  /**
   * The re-activation handler for a launch item key, or undefined when its owner
   * has none mounted (见 21). Undefined is the normal case — most rail items have
   * no such gesture — and the rail simply behaves as before.
   *
   * Already bound to its `itemId`, so the rail never has to take a projected
   * item's composite key apart to reconstruct an id the registration already had.
   */
  getRailActivation(key: string): (() => void) | undefined {
    return this.railActivations.get(key);
  }

  /**
   * Build the per-module UI activation context (架构方案 §5.7/§5.8, 见 17). Each
   * registration is tracked so {@link deactivateForView} can reclaim it on
   * teardown. Shared by view- and module-triggered activation.
   */
  private buildContext(moduleId: string): ModuleUiContext {
    const registered = new Set<string>();
    this.registeredViews.set(moduleId, registered);
    const registeredControllers = new Set<string>();
    this.registeredTabControllers.set(moduleId, registeredControllers);
    const registeredChrome = new Set<string>();
    this.registeredViewTabChrome.set(moduleId, registeredChrome);
    const registeredFragments = new Set<string>();
    this.registeredDetachables.set(moduleId, registeredFragments);
    const registeredContribs = new Set<string>();
    this.registeredContributions.set(moduleId, registeredContribs);
    return {
      registerView: (id, component) => {
        // Overlay views may declare the narrowed `OverlayViewProps`; mounting
        // always hands them full `ViewProps`, so storing them as `ViewComponent`
        // is sound (the narrower parameter is what blocks plain assignment).
        this.components.set(id, component as ViewComponent);
        registered.add(id);
      },
      registerTabController: (viewId, controller) => {
        this.tabControllers.set(viewId, controller);
        registeredControllers.add(viewId);
      },
      registerViewTabChrome: (viewId, chrome) => {
        this.viewTabChrome.set(viewId, chrome);
        registeredChrome.add(viewId);
      },
      registerDetachable: (fragmentId, renderer) => {
        this.detachables.set(fragmentId, renderer);
        registeredFragments.add(fragmentId);
      },
      registerContribution: (point, id, value) => {
        let byKey = this.contributionBindings.get(point);
        if (!byKey) {
          byKey = new Map();
          this.contributionBindings.set(point, byKey);
        }
        const key = `${moduleId}:${id}`;
        byKey.set(key, value);
        registeredContribs.add(`${point}::${key}`);
      },
      registerRailActivation: (itemId, handler) =>
        this.bindRailActivation(moduleId, itemId, handler),
    };
  }

  /**
   * Bind one rail re-activation handler, returning its disposer (见 21).
   *
   * Last registration wins, and the disposer only removes the handler it
   * installed. A view can remount while the outgoing instance has not run its
   * cleanup yet (workspace-key remounts do exactly this), and an unguarded
   * disposer would then delete the *incoming* instance's handler — leaving the
   * gesture silently dead with nothing to point at. Deliberately unlike
   * `registerLaunchBadgeProvider`, which throws on a duplicate: that registry
   * holds the contribution list and its providers live for a whole activation,
   * so a duplicate there really is a bug rather than an overlapping remount.
   */
  private bindRailActivation(
    moduleId: string,
    itemId: string,
    handler: RailActivationHandler,
  ): () => void {
    const key = launchItemKey(moduleId, itemId);
    const bound = (): void => handler({ itemId });
    this.railActivations.set(key, bound);
    return () => {
      if (this.railActivations.get(key) === bound) {
        this.railActivations.delete(key);
      }
    };
  }

  /**
   * Activate a module's UI entry once, de-duplicated per module. No-op (and not
   * cached, so a later call retries) when no `loader` is available. Shared core
   * of {@link activateForView} and {@link activateForModule}.
   */
  private activateModule(
    moduleId: string,
    loader: LoadUi | undefined,
  ): Promise<void> {
    let activation = this.activations.get(moduleId);
    if (!activation) {
      if (!loader) {
        return Promise.resolve();
      }
      const context = this.buildContext(moduleId);
      activation = (async () => {
        const exports = await loader();
        this.moduleExports.set(moduleId, exports);
        await exports.activate?.(context);
      })();
      this.activations.set(moduleId, activation);
    }
    return activation;
  }

  /**
   * Activate the UI module that owns `viewId` if not already done. Idempotent
   * and de-duplicated per module, so multiple views from the same module share
   * one activation. Records the view as visible (reference-counted teardown by
   * {@link deactivateForView}, 架构方案 §5.4).
   */
  async activateForView(viewId: string): Promise<void> {
    const view = this.views.find((candidate) => candidate.id === viewId);
    if (!view) {
      return;
    }
    const moduleId = view.moduleId;
    this.addVisible(moduleId, viewId);
    const loader = this.moduleUiLoader(moduleId) ?? this.runtimeLoader(view);
    await this.activateModule(moduleId, loader);
  }

  /**
   * Activate a module's UI purely to bind its contribution values (见 17), for a
   * contributor that has no visible view of its own. Prefers compile-time
   * loaders (in-repo glob); falls back to manifest `entry.ui` served over
   * `hibro-assets://` for downloaded / hybrid modules (见 12). Such a module has no
   * visibility refcount and stays UI-resident for the session (mirrors the
   * ext-host side, where resolving a contribution activates the provider and
   * leaves it active).
   */
  async activateForModule(moduleId: string): Promise<void> {
    const loader = this.moduleUiLoader(moduleId);
    if (!loader) {
      return;
    }
    this.residentModules.add(moduleId);
    await this.activateModule(moduleId, loader);
  }

  /**
   * Resolve the UI entry loader for `moduleId`: static glob first, then runtime
   * `entry.ui` (contribution satellite / downloaded source bundle).
   */
  private moduleUiLoader(moduleId: string): LoadUi | undefined {
    const staticLoader = this.loaders.get(moduleId);
    if (staticLoader) {
      return staticLoader;
    }
    const url = this.runtimeUiUrls.get(moduleId);
    if (!url) {
      return undefined;
    }
    return () => this.importUiModule(url);
  }

  /**
   * Mark `viewId` invisible; when all of its module's views are hidden, run the
   * module's UI `deactivate` hook, unregister its view components, and drop the
   * activation cache so a later activation re-registers cleanly (架构方案 §5.4).
   * No-op for unknown views or modules that are not active.
   */
  async deactivateForView(viewId: string): Promise<void> {
    const view = this.views.find((candidate) => candidate.id === viewId);
    if (!view) {
      return;
    }
    const moduleId = view.moduleId;
    const visible = this.visibleViews.get(moduleId);
    visible?.delete(viewId);
    if (!visible || visible.size > 0 || this.residentModules.has(moduleId)) {
      return;
    }

    const activation = this.activations.get(moduleId);
    if (!activation) {
      return;
    }
    // Ensure activation finished so components/exports exist before teardown.
    await activation;

    // The `await` above is a yield point: a remount (e.g. React StrictMode's
    // mount->unmount->mount, or quickly re-showing the view) may have made this
    // module's views visible again. If so, abort the teardown so we do not
    // unregister components the re-activation is relying on (which would strand
    // the view on "Loading…").
    const stillVisible = this.visibleViews.get(moduleId);
    if (stillVisible && stillVisible.size > 0) {
      return;
    }

    const exports = this.moduleExports.get(moduleId);
    const context: ModuleUiContext = {
      registerView: (id, component) => {
        this.components.set(id, component as ViewComponent);
      },
      registerTabController: (viewId, controller) => {
        this.tabControllers.set(viewId, controller);
      },
      registerViewTabChrome: (viewId, chrome) => {
        this.viewTabChrome.set(viewId, chrome);
      },
      registerDetachable: (fragmentId, renderer) => {
        this.detachables.set(fragmentId, renderer);
      },
      registerContribution: (point, id, value) => {
        let byKey = this.contributionBindings.get(point);
        if (!byKey) {
          byKey = new Map();
          this.contributionBindings.set(point, byKey);
        }
        byKey.set(`${moduleId}:${id}`, value);
      },
      registerRailActivation: (itemId, handler) =>
        this.bindRailActivation(moduleId, itemId, handler),
    };
    await exports?.deactivate?.(context);

    for (const id of this.registeredViews.get(moduleId) ?? []) {
      this.components.delete(id);
    }
    for (const viewId of this.registeredTabControllers.get(moduleId) ?? []) {
      this.tabControllers.delete(viewId);
    }
    for (const viewId of this.registeredViewTabChrome.get(moduleId) ?? []) {
      this.viewTabChrome.delete(viewId);
    }
    for (const fragmentId of this.registeredDetachables.get(moduleId) ?? []) {
      this.detachables.delete(fragmentId);
    }
    // Reclaim renderer-side contribution bindings this module made (见 17).
    for (const compound of this.registeredContributions.get(moduleId) ?? []) {
      const sep = compound.indexOf('::');
      const point = compound.slice(0, sep);
      const key = compound.slice(sep + 2);
      const byKey = this.contributionBindings.get(point);
      byKey?.delete(key);
      if (byKey && byKey.size === 0) {
        this.contributionBindings.delete(point);
      }
    }
    this.registeredViews.delete(moduleId);
    this.registeredTabControllers.delete(moduleId);
    this.registeredViewTabChrome.delete(moduleId);
    this.registeredDetachables.delete(moduleId);
    this.registeredContributions.delete(moduleId);
    this.moduleExports.delete(moduleId);
    this.activations.delete(moduleId);
    this.visibleViews.delete(moduleId);
  }

  /**
   * Runtime UI loader for a downloaded inline view (见 12): its UI bundle is
   * fetched from the `hibro-assets://<id>/<entry.ui>` URL the host resolved onto the
   * view and imported as an ES module, since it is absent from the shell's
   * compile-time glob. Returns undefined for compiled-in modules (served by the
   * static `loaders`) and for non-inline surfaces (iframe/external/remote never
   * render inline-dom through this registry), so they fall through unchanged.
   */
  private runtimeLoader(view: ResolvedView): LoadUi | undefined {
    if (view.kind !== 'inline' || !view.url) {
      return undefined;
    }
    const url = view.url;
    return () => this.importUiModule(url);
  }

  private addVisible(moduleId: string, viewId: string): void {
    let visible = this.visibleViews.get(moduleId);
    if (!visible) {
      visible = new Set();
      this.visibleViews.set(moduleId, visible);
    }
    visible.add(viewId);
  }
}
